/**
 * AmbientDashboard — Split-screen simultaneous multi-panel layout.
 *
 * DRR-183 Phase 3: Spatial (left) + Kanban (right) + AlertTicker (bottom).
 * No rotation — all views visible simultaneously.
 * Large-font, high-contrast styling for 3-5m viewing distance on 1080p TVs.
 *
 * Layout (CSS Grid):
 * ┌─────────────┬─────────────┐
 * │  Spatial     │  Kanban      │
 * │  (By Room)   │  (By Status) │
 * │              │              │
 * ├──────────────┴──────────────┤
 * │  Alert Ticker (scrolling)   │
 * └─────────────────────────────┘
 */

import { useMemo } from 'react';
import { Box, Text, Group, Badge } from '@mantine/core';
import { IconMap, IconLayoutKanban } from '@tabler/icons-react';
import { SpatialRenderer, type SpatialCoordinate } from '../renderers/SpatialRenderer';
import { KanbanRenderer, type KanbanFieldConfig } from '../renderers/KanbanRenderer';
import { AlertTicker, type AlertTickerItem } from './AlertTicker';
import type { PrimitiveItem } from '../../hooks/usePrimitiveData';

export interface DashboardPanelConfig {
  slot: 'left' | 'right' | 'bottom';
  renderer: 'spatial' | 'kanban';
  dimension: string;
}

interface AmbientDashboardProps {
  items: PrimitiveItem[];
  alerts: AlertTickerItem[];
  panels?: DashboardPanelConfig[];
  coordinates?: SpatialCoordinate[];
  floorPlanUrl?: string;
}

const DEFAULT_PANELS: DashboardPanelConfig[] = [
  { slot: 'left', renderer: 'spatial', dimension: 'location' },
  { slot: 'right', renderer: 'kanban', dimension: 'status' },
];

function spatialFieldAdapter(item: PrimitiveItem) {
  return {
    name: (item.name as string) ?? 'Unnamed',
    subtitle: (item.status as string) ?? undefined,
    safetyLevel: item.safetyLevel as string,
  };
}

function kanbanFieldAdapter(item: PrimitiveItem): KanbanFieldConfig {
  return {
    name: (item.name as string) ?? 'Unnamed',
    subtitle: (item.description as string) ?? undefined,
    status: item.status as string,
    safetyLevel: item.safetyLevel as string,
  };
}

function PanelHeader({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Group gap="xs" mb="xs">
      <Badge variant="light" size="lg" leftSection={icon}>
        {label}
      </Badge>
    </Group>
  );
}

function RenderPanel({
  panel,
  items,
  maxHeight,
  coordinates,
  floorPlanUrl,
}: {
  panel: DashboardPanelConfig;
  items: PrimitiveItem[];
  maxHeight: string;
  coordinates?: SpatialCoordinate[];
  floorPlanUrl?: string;
}) {
  switch (panel.renderer) {
    case 'spatial':
      return (
        <Box style={{ height: '100%' }}>
          <PanelHeader icon={<IconMap size={14} />} label="By Location" />
          <SpatialRenderer
            items={items}
            locationField={panel.dimension}
            fieldAdapter={spatialFieldAdapter}
            getId={(item) => item.id as string}
            maxHeight={maxHeight}
            coordinates={coordinates}
            floorPlanUrl={floorPlanUrl}
          />
        </Box>
      );
    case 'kanban':
      return (
        <Box style={{ height: '100%' }}>
          <PanelHeader icon={<IconLayoutKanban size={14} />} label="By Status" />
          <KanbanRenderer
            items={items}
            columnField={panel.dimension}
            fieldAdapter={kanbanFieldAdapter}
            getId={(item) => item.id as string}
            maxHeight={maxHeight}
          />
        </Box>
      );
    default:
      return <Text c="dimmed">Unknown renderer: {panel.renderer}</Text>;
  }
}

export function AmbientDashboard({
  items,
  alerts,
  panels: panelOverrides,
  coordinates,
  floorPlanUrl,
}: AmbientDashboardProps) {
  const panels = panelOverrides ?? DEFAULT_PANELS;

  const leftPanel = useMemo(
    () => panels.find((p) => p.slot === 'left'),
    [panels],
  );
  const rightPanel = useMemo(
    () => panels.find((p) => p.slot === 'right'),
    [panels],
  );

  const panelMaxHeight = 'calc(100vh - 140px)';

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr auto',
        height: '100%',
        gap: 'var(--mantine-spacing-sm)',
        fontSize: 'clamp(0.85rem, 1.2vw, 1.4rem)',
      }}
    >
      {/* Left panel */}
      <Box
        p="sm"
        style={{
          gridColumn: leftPanel && rightPanel ? '1 / 2' : '1 / -1',
          gridRow: '1 / 2',
          overflow: 'hidden',
        }}
      >
        {leftPanel ? (
          <RenderPanel
            panel={leftPanel}
            items={items}
            maxHeight={panelMaxHeight}
            coordinates={coordinates}
            floorPlanUrl={floorPlanUrl}
          />
        ) : (
          <Text c="dimmed">No left panel configured</Text>
        )}
      </Box>

      {/* Right panel */}
      {rightPanel && (
        <Box
          p="sm"
          style={{
            gridColumn: '2 / 3',
            gridRow: '1 / 2',
            overflow: 'hidden',
          }}
        >
          <RenderPanel
            panel={rightPanel}
            items={items}
            maxHeight={panelMaxHeight}
          />
        </Box>
      )}

      {/* Bottom ticker */}
      <Box style={{ gridColumn: '1 / -1', gridRow: '2 / 3' }}>
        <AlertTicker alerts={alerts} />
      </Box>
    </Box>
  );
}
